"use client";
import React, { useState } from "react";
import "./ContactForm.scss";
import { AiFillCloseCircle } from "react-icons/ai";

const ContactForm = ({ onClose, backgroundColor = "#D62314" }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNo: "",
    details: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    mobileNo: "",
    details: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    mobileNo: false,
    details: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [apiResponse, setApiResponse] = useState("");

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...errors };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    } else {
      newErrors.name = "";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
      valid = false;
    } else {
      newErrors.email = "";
    }

    // Mobile number validation
    if (!formData.mobileNo.trim()) {
      newErrors.mobileNo = "Mobile number is required";
      valid = false;
    } else if (!formData.mobileNo.startsWith("03")) {
      newErrors.mobileNo = "Mobile number must start with 03";
      valid = false;
    } else if (!/^03\d{9}$/.test(formData.mobileNo)) {
      newErrors.mobileNo = "Please enter a valid phone number (11 digits)";
      valid = false;
    } else {
      newErrors.mobileNo = "";
    }

    // Details validation
    if (!formData.details.trim()) {
      newErrors.details = "Message is required";
      valid = false;
    } else if (formData.details.trim().length < 10) {
      newErrors.details = "Message must be at least 10 characters";
      valid = false;
    } else {
      newErrors.details = "";
    }

    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobileNo") {
      const numericValue = value.replace(/[^0-9]/g, "");
      if (numericValue.length <= 11) {
        setFormData({
          ...formData,
          [name]: numericValue,
        });
      }
    } else if (name === "name") {
      // Allow only letters and spaces, no numbers or special characters
      const alphabeticValue = value.replace(/[^a-zA-Z\s]/g, "");
      setFormData({
        ...formData,
        [name]: alphabeticValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitStatus(null);
      setApiResponse("");

      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND}/storefront/customer_inquiry`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              name: formData.name,
              email: formData.email,
              phone: formData.mobileNo,
              details: formData.details,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          console.log("API response:", data);
          setSubmitStatus("success");
          setApiResponse(data.detail || "Thank you! We will contact you soon.");
          setTimeout(() => {
            onClose();
          }, 3000);
        } else {
          console.error("API error:", response.status);
          setSubmitStatus("error");
          setApiResponse("Failed to submit form. Please try again.");
        }
      } catch (error) {
        console.error("Request failed:", error);
        setSubmitStatus("error");
        setApiResponse("An error occurred. Please try again later.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleNumericKeyDown = (e) => {
    if (
      [8, 46, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 67 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 88 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode === 86 && (e.ctrlKey === true || e.metaKey === true)) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }

    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const renderContent = () => {
    if (submitStatus === "success") {
      return (
        <div className="contact-form__success-container">
          <div className="contact-form__success-icon">
            <svg
              width="80"
              height="80"
              viewBox="0 0 80 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="40" cy="40" r="40" fill={backgroundColor} />
              <path
                d="M35 50L28 43M35 50L52 33"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="contact-form__success-message">{apiResponse}</p>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="contact-form__content">
        <h2 className="contact-form__title">Contact Us</h2>

        <div className="contact-form__field">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your name"
            className={errors.name && touched.name ? "error" : ""}
          />
          {errors.name && touched.name && (
            <p className="contact-form__error">{errors.name}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="email">
            Email <span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your email"
            className={errors.email && touched.email ? "error" : ""}
          />
          {errors.email && touched.email && (
            <p className="contact-form__error">{errors.email}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="mobileNo">
            Mobile Number <span className="required">*</span>
          </label>
          <input
            type="text"
            id="mobileNo"
            name="mobileNo"
            value={formData.mobileNo}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleNumericKeyDown}
            placeholder="03XXXXXXXXX"
            maxLength={11}
            inputMode="numeric"
            className={errors.mobileNo && touched.mobileNo ? "error" : ""}
          />
          {errors.mobileNo && touched.mobileNo && (
            <p className="contact-form__error">{errors.mobileNo}</p>
          )}
        </div>

        <div className="contact-form__field">
          <label htmlFor="details">
            Message <span className="required">*</span>
          </label>
          <textarea
            id="details"
            name="details"
            value={formData.details}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Enter your message (minimum 10 characters)"
            rows="4"
            className={errors.details && touched.details ? "error" : ""}
          />
          {errors.details && touched.details && (
            <p className="contact-form__error">{errors.details}</p>
          )}
        </div>

        <button
          type="submit"
          className="contact-form__submit-btn"
          disabled={isSubmitting}
          style={{ backgroundColor }}
        >
          {isSubmitting ? "Submitting..." : "Send Message"}
        </button>

        {submitStatus === "error" && (
          <p className="contact-form__status contact-form__status--error">
            {apiResponse}
          </p>
        )}
      </form>
    );
  };

  return (
    <div className="contact-form" style={{ "--background-color": backgroundColor }}>
      <div className="contact-form__header">
        <h3>How may I help you?</h3>
        <button
          className="contact-form__close-btn"
          onClick={onClose}
          aria-label="Close form"
        >
          <AiFillCloseCircle size={24} />
        </button>
      </div>

      <div className="contact-form__card">{renderContent()}</div>
    </div>
  );
};

export default ContactForm;
